/**
 * App-wide Circuit Breaker for API request management
 * Prevents cascading failures and multiple error notifications when server is unavailable
 */

import { NotificationManagerAPI, useNotificationManager } from "notifications/NotificationManager";
import React, { useEffect, useState } from "react";

export interface CircuitBreakerState {
    isOpen: boolean;
    failureCount: number;
    lastFailureTime: number | null;
    activeServerUnavailableNotificationId: string | null;
}

export interface CircuitBreakerStatus {
    isOpen: boolean;
    failureCount: number;
    timeUntilRetry?: number;
    hasActiveNotification?: boolean;
}

export class CircuitBreaker {
    private state: CircuitBreakerState = {
        isOpen: false,
        failureCount: 0,
        lastFailureTime: null,
        activeServerUnavailableNotificationId: null
    };

    private readonly failureThreshold: number;
    private readonly recoveryTimeoutMs: number;
    private notificationManagerRef: NotificationManagerAPI | null = null;

    constructor(config?: {
        failureThreshold?: number;
        recoveryTimeoutMs?: number;
    }) {
        this.failureThreshold = config?.failureThreshold ?? 1; // Open after first failure
        this.recoveryTimeoutMs = config?.recoveryTimeoutMs ?? 30000; // 30 seconds (fallback only)
    }

    /**
     * Set the global notification manager reference
     * This should be called from the App level when the NotificationManager is available
     */
    public setNotificationManager(notificationManager: NotificationManagerAPI): void {
        this.notificationManagerRef = notificationManager;
    }

    private reset(): void {
        // If we have an active notification being tracked, remove it before clearing the ID
        if (this.state.activeServerUnavailableNotificationId && this.notificationManagerRef) {
            console.log(`[CircuitBreaker] Removing server unavailable notification: ${this.state.activeServerUnavailableNotificationId}`);
            this.notificationManagerRef.removeNotification(this.state.activeServerUnavailableNotificationId);
        }

        this.state = {
            isOpen: false,
            failureCount: 0,
            lastFailureTime: null,
            activeServerUnavailableNotificationId: null
        };
        console.log("[CircuitBreaker] Reset - circuit closed");
    }

    /**
     * Check if a user notification should be shown when circuit is open
     * @returns true if error notification should be shown, false if suppressed
     */
    public shouldShowCircuitOpenNotification(): boolean {
        if (!this.state.isOpen) {
            return false;
        }

        // If we have a notification manager, check if our tracked notification still exists
        if (this.notificationManagerRef && this.state.activeServerUnavailableNotificationId) {
            const stillExists = this.notificationManagerRef.hasId(this.state.activeServerUnavailableNotificationId);
            if (stillExists) {
                // Notification is still active, don't show another one
                return false;
            }
            else {
                // Notification was removed, clear our tracking
                this.state.activeServerUnavailableNotificationId = null;
            }
        }

        // Show notification if we don't have an active one
        return this.state.activeServerUnavailableNotificationId === null;
    }

    /**
     * Record that a server unavailable notification was shown
     * @param notificationId The ID returned by the notification manager
     */
    public recordServerUnavailableNotification(notificationId: string): void {
        this.state.activeServerUnavailableNotificationId = notificationId;
        console.log(`[CircuitBreaker] Tracking server unavailable notification: ${notificationId}`);
    }

    /**
     * Check if requests should be allowed through the circuit breaker
     * @returns true if requests are allowed, false if circuit is open
     */
    public canMakeRequest(): boolean {
        const now = Date.now();

        // If circuit is closed, allow request
        if (!this.state.isOpen) {
            return true;
        }

        // Primary recovery mechanism: Check if user dismissed the notification
        // If there's no active server unavailable notification, close the circuit immediately
        if (this.notificationManagerRef && this.state.activeServerUnavailableNotificationId) {
            const stillExists = this.notificationManagerRef.hasId(this.state.activeServerUnavailableNotificationId);
            if (!stillExists) {
                console.log("[CircuitBreaker] Server unavailable notification was dismissed by user - closing circuit immediately");
                this.reset();
                return true;
            }
        }
        else if (this.state.activeServerUnavailableNotificationId === null) {
            // No tracked notification exists, close the circuit
            console.log("[CircuitBreaker] No active server unavailable notification - closing circuit");
            this.reset();
            return true;
        }

        // Fallback recovery mechanism: If enough time has passed, try to close circuit
        // This is now a secondary mechanism in case notification tracking fails
        if (this.state.lastFailureTime &&
            (now - this.state.lastFailureTime) > this.recoveryTimeoutMs) {
            console.log("[CircuitBreaker] Recovery timeout elapsed (fallback mechanism) - attempting to close circuit");
            this.reset();
            return true;
        }

        // Circuit is open and we still have an active notification
        console.log("[CircuitBreaker] Circuit is open, blocking request - server unavailable notification still active");
        return false;
    }

    /**
     * Record a successful API call - resets circuit breaker and automatically clears server unavailable notifications
     */
    public recordSuccess(): void {
        const wasOpen = this.state.isOpen;
        const hadActiveNotification = this.state.activeServerUnavailableNotificationId !== null;

        if (this.state.failureCount > 0 || wasOpen || hadActiveNotification) {
            console.log("[CircuitBreaker] Success recorded, resetting circuit"
                + (hadActiveNotification ? " and clearing server unavailable notification" : ""));
            this.reset();

            // If we had an active notification, log that connectivity has been restored
            if (hadActiveNotification) {
                console.log("[CircuitBreaker] Server connectivity restored - notification automatically cleared");
            }
        }
    }

    /**
     * Record a failed API call - may open the circuit breaker
     * @returns true if error notification should be shown, false if suppressed
     */
    public recordFailure(): boolean {
        const now = Date.now();
        this.state.failureCount++;
        this.state.lastFailureTime = now;

        if (this.state.failureCount >= this.failureThreshold) {
            this.state.isOpen = true;
            console.log(`[CircuitBreaker] Circuit opened after ${this.state.failureCount} failures`);
        }

        return this.shouldShowCircuitOpenNotification();
    }

    /**
     * Get current circuit breaker status
     */
    public getStatus(): CircuitBreakerStatus {
        const now = Date.now();
        const timeUntilRetry = this.state.lastFailureTime
            ? Math.max(0, this.recoveryTimeoutMs - (now - this.state.lastFailureTime))
            : 0;

        return {
            isOpen: this.state.isOpen,
            failureCount: this.state.failureCount,
            hasActiveNotification: this.state.activeServerUnavailableNotificationId !== null,
            ...(this.state.isOpen && { timeUntilRetry })
        };
    }

    /**
     * Force reset the circuit breaker (useful for page refresh)
     */
    public forceReset(): void {
        console.log("[CircuitBreaker] Force reset requested");
        this.reset();
    }

    /**
     * Get internal state for debugging
     */
    public getDebugInfo(): CircuitBreakerState {
        return { ...this.state };
    }
}

// Global circuit breaker instance
export const circuitBreaker = new CircuitBreaker({
    failureThreshold: 1,      // Open circuit after first failure
    recoveryTimeoutMs: 30000 // Fallback recovery after 30 seconds (primary is notification dismissal)
});

// ---=== CircuitBreakerProvider ===--- //

/**
 * Component that initializes the global circuit breaker with the notification manager reference
 * This should be used at the app level to ensure the circuit breaker can manage notifications
 */
export const CircuitBreakerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const notificationManager = useNotificationManager();

    useEffect(() => {
        // Set the global notification manager reference in the circuit breaker
        circuitBreaker.setNotificationManager(notificationManager);
        console.log("[CircuitBreakerProvider] Circuit breaker initialized with notification manager");
    }, [notificationManager]);

    return <>{children}</>;
};

// ---=== Custom Hooks ===--- //

/**
 * React hook to monitor circuit breaker status
 * Provides real-time updates on circuit breaker state for UI components
 */
export const useCircuitBreakerStatus = (updateIntervalMs: number = 1000) => {
    const [status, setStatus] = useState<CircuitBreakerStatus>(() =>
        circuitBreaker.getStatus()
    );

    useEffect(() => {
        const interval = setInterval(() => {
            const newStatus = circuitBreaker.getStatus();
            setStatus(prevStatus => {
                // Only update if something actually changed to prevent unnecessary re-renders
                if (
                    prevStatus.isOpen !== newStatus.isOpen
                    || prevStatus.failureCount !== newStatus.failureCount
                    || (prevStatus.timeUntilRetry ?? 0) !== (newStatus.timeUntilRetry ?? 0)
                ) {
                    return newStatus;
                }
                return prevStatus;
            });
        }, updateIntervalMs);

        return () => clearInterval(interval);
    }, [updateIntervalMs]);

    return {
        ...status,
        // Helper computed properties
        isHealthy: !status.isOpen && status.failureCount === 0,
        isRecovering: status.isOpen && (status.timeUntilRetry ?? 0) > 0,
        canRetryNow: status.isOpen && (status.timeUntilRetry ?? 0) === 0,

        // Helper methods
        getStatusText: (): string => {
            if (!status.isOpen && status.failureCount === 0) {
                return "Healthy";
            }
            if (status.isOpen && status.timeUntilRetry && status.timeUntilRetry > 0) {
                const seconds = Math.ceil(status.timeUntilRetry / 1000);
                return `Recovering (${seconds}s remaining)`;
            }
            if (status.isOpen) {
                return "Circuit Open - Ready to retry";
            }
            return `${status.failureCount} failure(s)`;
        },

        forceReset: () => circuitBreaker.forceReset()
    };
};
/**
 * Lightweight hook that only tracks if circuit breaker is open
 * Use this for performance-critical components that only need open/closed state
 */
export const useCircuitBreakerOpen = (updateIntervalMs: number = 1000) => {
    const [isOpen, setIsOpen] = useState<boolean>(() =>
        circuitBreaker.getStatus().isOpen
    );

    useEffect(() => {
        const interval = setInterval(() => {
            const newIsOpen = circuitBreaker.getStatus().isOpen;
            setIsOpen(prevIsOpen => {
                return prevIsOpen !== newIsOpen ? newIsOpen : prevIsOpen;
            });
        }, updateIntervalMs);

        return () => clearInterval(interval);
    }, [updateIntervalMs]);

    return isOpen;
};